import React, { useEffect } from 'react';
import { Textarea } from './ui/textarea';

/**
 * Cycle of Addiction Worksheet  --  Week 3 SW.
 *
 * Renders the SuperAdmin-uploaded blank worksheet image at the top, then
 * eight labelled boxes the user fills in with their own experience for
 * each stage of the cycle.  All eight inputs are stored as a single JSON
 * string in the parent answer  -- so it integrates seamlessly with the
 * existing draft autosave and complete flow.
 *
 * The eight stages are pinned to the source PDF terminology:
 *   Content & Stable, Stressor, RID (Restless / Irritable / Discontented),
 *   First Use, Drinking / Drug Spree, Emerging Remorseful, Obsession Returns,
 *   Compulsion Returns.
 */

const STAGES = [
  { id: 'content', label: 'Content & Stable', help: 'A period when life felt steady, before the cycle began again.' },
  { id: 'stressor', label: 'Stressor', help: 'The trigger or pressure that landed before you started thinking about using.' },
  { id: 'rid', label: 'Restless · Irritable · Discontented', help: 'The internal turbulence that built up before the first use.' },
  { id: 'first_use', label: 'First Use', help: 'The moment you took the first drink or drug after a clean / sober period.' },
  { id: 'spree', label: 'Drinking / Drug Spree', help: 'The full course of the use after the first drink or drug took control.' },
  { id: 'remorseful', label: 'Emerging Remorseful', help: 'The shame, regret and "never again" period that followed.' },
  { id: 'obsession', label: 'Obsession Returns', help: 'When thoughts of using crept back in, often dressed up as something else.' },
  { id: 'compulsion', label: 'Compulsion Returns', help: 'When the obsession turned into action and the cycle restarted.' },
];

function _backendUrl() {
  return process.env.REACT_APP_BACKEND_URL?.replace(/\/+$/, '') || '';
}

export default function CycleOfAddictionWorksheet({ slug, imageUrl, value, onChange, testid = 'cycle-of-addiction' }) {
  // value is a JSON string keyed by stage id;  guard against bad input.
  let stored = {};
  try { stored = value ? JSON.parse(value) : {}; } catch { stored = {}; }

  const setStage = (sid, txt) => {
    const next = { ...stored, [sid]: txt };
    onChange?.(JSON.stringify(next));
  };

  // Auto-mark required by emitting a single combined string when ALL stages
  // have at least 4 characters.  The parent only checks for non-empty.
  useEffect(() => {
    if (!value) onChange?.(JSON.stringify({}));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only initialiser

  const fullImage = imageUrl?.startsWith('/') ? `${_backendUrl()}${imageUrl}` : imageUrl;

  const filledCount = STAGES.filter(s => (stored[s.id] || '').trim().length >= 4).length;

  return (
    <div className="space-y-3" data-testid={testid}>
      {fullImage && (
        <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-2">
          <img
            src={fullImage}
            alt="The Cycle of Addiction blank worksheet template"
            className="w-full max-w-md mx-auto rounded"
            data-testid={`${testid}-image`}
          />
          <p className="text-[10px] text-zinc-500 text-center mt-1.5">Reference template.  Fill the stages below in your own words.</p>
        </div>
      )}

      <div className="flex items-center justify-between text-[11px] text-zinc-600">
        <span><strong>{filledCount}/8</strong> stages complete</span>
        <span className="text-zinc-400">your work auto-saves every 5 seconds</span>
      </div>

      <div className="grid grid-cols-1 gap-2">
        {STAGES.map((stage, i) => (
          <div key={stage.id} className="bg-white border border-zinc-200 rounded-lg p-3" data-testid={`${testid}-stage-${stage.id}`}>
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-6 h-6 rounded-full bg-zinc-900 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">{i + 1}</div>
              <div className="flex-1">
                <p className="text-[11px] font-bold text-zinc-900 leading-tight">{stage.label}</p>
                <p className="text-[10px] text-zinc-500 leading-tight">{stage.help}</p>
              </div>
            </div>
            <Textarea
              value={stored[stage.id] || ''}
              onChange={(e) => setStage(stage.id, e.target.value)}
              placeholder="Your experience at this stage..."
              rows={2}
              className="resize-y bg-zinc-50 border-zinc-200 text-sm"
              data-testid={`${testid}-input-${stage.id}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
